import React                          from 'react';
import classNames                     from 'classnames';
import { Observe }                    from 'rewire-core';
import {
  Theme,
  CSSTheme,
  WithStyle,
}                                     from 'rewire-ui';
import Button                         from '@material-ui/core/Button';
import Icon                           from '@material-ui/core/Icon';
import AutorenewIcon                  from '@material-ui/icons/Autorenew';
import { IViewTab }                   from '../ui/ViewTabs';
import ViewTabs                       from '../ui/ViewTabs';
import { pluckStartEnd }              from '../utility/DateUtils';
import Breadcrumbs                    from '../main-navigation/Breadcrumbs';
import MainContentToolbar             from '../main-navigation/MainContentToolbar';
import ScheduleControls               from '../schedule/ScheduleControls';
import { confirmationModel }          from '../ui/ConfirmationDialog';
import AddTestDialog                  from './AddTimecardTestDialog';
import timecardTestEditTimecardsModel from './editTimecard/TimecardTestEditTimecardsModel';
import timecardTestModel, {
  TestViewType
}                                     from './TimecardTestModel';
import { withStyles }                 from '../utility/styles';

const styles = (theme: Theme) => ({
  left: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flex: '0 1 auto',
    flexWrap: 'wrap',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: '0 1 auto',
    flexWrap: 'wrap',
  },
  button: {
    textTransform: 'none',
    minWidth: '0px',
    flex: '0 0 auto',
    marginLeft: '10px',
    marginBottom: '7px',
  },
  editTimecardsButton: {
    minWidth: '100px',
  },
  undoButton: {
  },
  saveButton: {
    marginRight: '5px',
    color: theme.palette.success.contrastText,
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.light,
    },
  },
  runRulesButton: {
    minWidth: 48,
    width: 48,
    height: 29,
    marginLeft: '66px',
    '& span': {
      margin: 0
    },
    color: theme.palette.warning.contrastText,
    backgroundColor: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.light,
    },
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover, &:active': {
      backgroundColor: theme.palette.error.light,
    }
  },
  updateButton: {
    backgroundColor: theme.palette.warning.main,
    '&:hover, &:active': {
      backgroundColor: theme.palette.warning.light,
    }
  },
  addButton: {
    padding: '4px 16px',
    marginRight: '5px',
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    '&:hover, &:active': {
      backgroundColor: theme.palette.success.light,
    }
  },
  viewTabsRoot: {
    marginBottom: '7px',
  },
  viewTabsViewButton: {
    paddingTop: '5px',
    paddingBottom: '5px',
  },
}) as CSSTheme;

interface ITimecardTestToolbarProps {
}

type TimecardTestToolbarProps = WithStyle<ReturnType<typeof styles>, ITimecardTestToolbarProps>;

class TimecardTestToolbar extends React.PureComponent<TimecardTestToolbarProps> {
  viewTabs: IViewTab[];

  constructor(props: TimecardTestToolbarProps) {
    super(props);

    this.viewTabs = [
      {title: 'Summary',       disabled: () => timecardTestModel.isView('Summary'),       onClick: this.handleCardViewClick('Summary'),       content: 'Summary'},
      {title: 'Edit Timecard', disabled: () => timecardTestModel.isView('Edit Timecard'), onClick: this.handleCardViewClick('Edit Timecard'), content: 'Edit Timecard'},
    ];
  }

  handleCardViewClick = (view: TestViewType) => () => {
    timecardTestModel.view = view;
  };

  handleUndo = () => {
    confirmationModel.load(
      `Please confirm you wish to undo the changes you have made to the timecard for ${timecardTestEditTimecardsModel.selectedEmployee!.name} during ${pluckStartEnd(timecardTestEditTimecardsModel.effective!)}`,
      {
        yesAction: () => {
          timecardTestModel.cancel();
          return true;
        },
        noAction: () => true,
        maxWidth: 'sm', title: 'Confirm Cancel Timecard Changes'
      }
    );
  };

  handleRunRules = () => {
    timecardTestModel.runRules();
  };

  handleSave = () => {
    timecardTestModel.save();
  };

  handleCreate = () => {
    timecardTestModel.createNewTimecardTest();
  };

  handleDelete = () => {
    timecardTestModel.deleteTimecardTest();
  };

  render() {
    const { classes } = this.props;

    return (
      <MainContentToolbar>
        <div className={classes.left}>
          <Breadcrumbs />
        </div>

        <ViewTabs tabs={this.viewTabs} classes={{root: classes.viewTabsRoot, viewButton: classes.viewTabsViewButton}} />

        <div className={classes.right}>
          <Observe render={() => (
            timecardTestModel.isView('Edit Timecard')
              ? < >
                  <Observe render={() => <Button className={classNames(classes.button, classes.editTimecardsButton, classes.runRulesButton)} variant='contained' disabled={!timecardTestModel.canRunRules()} onClick={this.handleRunRules} size='small' title={'Run Rules'} startIcon={<AutorenewIcon />}></Button>} />
                  <Observe render={() => <Button className={classNames(classes.button, classes.editTimecardsButton, classes.undoButton)}     variant='contained' disabled={!timecardTestModel.canSave()}     onClick={this.handleUndo} title='Undo'>Undo</Button>} />
                  <Observe render={() => <Button className={classNames(classes.button, classes.editTimecardsButton, classes.saveButton)}     variant='contained' disabled={!timecardTestModel.canSave()}     onClick={this.handleSave} title='Save'>Save</Button>} />
                </>
              : < >
                  <Observe render={() => (
                    <Button disabled={!timecardTestModel.canDelete()} onClick={this.handleDelete} className={classNames(classes.button, classes.deleteButton)} variant='contained' title='Delete Test'>
                      <span>Delete Test</span>
                    </Button>
                  )} />
                  <Observe render={() => (
                    <Button disabled={!timecardTestModel.canUpdate()} onClick={this.handleSave} className={classNames(classes.button, classes.updateButton)} variant='contained' title='Update Test'>
                      <span>Update Test</span>
                    </Button>
                  )} />
                  <Button variant='contained' disabled={!timecardTestModel.canAdd()} onClick={this.handleCreate} className={classNames(classes.button, classes.addButton)} title='Add Test'>
                    <Icon className='add'>{'add'}</Icon>
                    <span>Add Test</span>
                  </Button>
                </>
          )} />
          <ScheduleControls hideGroupBy={true} hideSortBy={false} />
        </div>
        <AddTestDialog />
      </MainContentToolbar>
    );
  }
}

export default withStyles<ITimecardTestToolbarProps>(styles, TimecardTestToolbar);
